"use client";

import { useState, useRef, useEffect } from "react";
import { Copy, Check } from "@phosphor-icons/react/dist/ssr";
import { useTranslations } from "next-intl";

/**
 * Small copy-to-clipboard button, sized to sit beside a value (phone / email)
 * on the navy contact card. Swaps to a gold check for ~1.5s on success, flips
 * its accessible name to "Copied", and announces the same via an aria-live
 * region (a name change alone isn't reliably re-announced on a focused button).
 * Client-only — `navigator.clipboard` needs the browser; on a clipboard-less
 * context the click is a silent no-op.
 */
export default function CopyButton({ value, ariaLabel, tone = "dark" }: { value: string; ariaLabel: string; tone?: "dark" | "light" }) {
  const t = useTranslations("profile");
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear any pending revert on unmount so we never setState after unmount.
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (timer.current) clearTimeout(timer.current); // reset on rapid re-click
      timer.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard unavailable (insecure context / denied) — no-op.
    }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={copied ? t("copied") : ariaLabel}
      // `tone="light"` for white surfaces (admin referrals); the default is the
      // navy contact card this was built for.
      className={`shrink-0 rounded-control p-5 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold ${tone === "light" ? "text-gray-500 hover:text-navy" : "text-white/50 hover:text-gold"}`}
    >
      {copied ? (
        /* GREEN on success (owner, 2026-08-26): the tick CONFIRMS the copy
           happened, so it reads green, not brand gold. `#10B981` because this
           button sits on the dark profile panel (base is text-white/50). The
           gold hover/focus ring above is unchanged — that is interaction
           affordance, not confirmation. */
        <Check size={14} weight="bold" aria-hidden="true" className={tone === "light" ? "text-green-strong" : "text-green"} />
      ) : (
        <Copy size={14} weight="regular" aria-hidden="true" />
      )}
      <span aria-live="polite" className="sr-only">{copied ? t("copied") : ""}</span>
    </button>
  );
}
